import { useState, useEffect } from "react";
import { AuthContext, type AuthUser, type SocialProvider } from "./AuthContext";
import { authClient } from "./client";
import { claimAnonymousTrips } from "../api/trips";

const ANON_UID_KEY = "daynav.anonUid";

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const claimIfAnonymous = async () => {
    try {
      const anonUid = localStorage.getItem(ANON_UID_KEY);
      if (anonUid) {
        localStorage.removeItem(ANON_UID_KEY);
        await claimAnonymousTrips(anonUid);
      }
    } catch {}
  };

  useEffect(() => {
    authClient.getSession().then(async ({ data }) => {
      if (!data?.user) {
        setUser(null);
        setIsLoading(false);
        return;
      }
      try {
        const res = await fetch("/api/me", { credentials: "include" });
        if (res.ok) {
          const body = (await res.json()) as { user: AuthUser };
          setUser(body.user);
          if (!body.user.isAnonymous) {
            await claimIfAnonymous();
          }
        } else {
          setUser(data.user as AuthUser);
        }
      } catch {
        setUser(data.user as AuthUser);
      }
      setIsLoading(false);
    });
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await authClient.signIn.email({ email, password });
    if (error) throw new Error(error.message);
    setUser(data.user as AuthUser);
    await claimIfAnonymous();
  };

  const signUp = async (email: string, password: string, name: string) => {
    const { error } = await authClient.signUp.email({
      email,
      password,
      name,
    });
    if (error) throw new Error(error.message);
  };

  const signOut = async () => {
    await authClient.signOut();
    setUser(null);
  };

  const requestPasswordReset = async (email: string) => {
    const { error } = await authClient.requestPasswordReset({
      email,
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw new Error(error.message);
  };

  const resetPassword = async (token: string, newPassword: string) => {
    const { error } = await authClient.resetPassword({ token, newPassword });
    if (error) throw new Error(error.message);
  };

  const signInWithSocial = async (provider: SocialProvider, returnTo?: string) => {
    await authClient.signIn.social({
      provider,
      callbackURL: window.location.origin + (returnTo || "/"),
    });
  };

  const signInAnonymous = async (): Promise<AuthUser> => {
    const { data, error } = await authClient.signIn.anonymous();
    if (error) throw new Error(error.message);
    const u: AuthUser = {
      id: data.user.id,
      email: data.user.email,
      name: data.user.name,
      isAnonymous: true,
    };
    setUser(u);
    try { localStorage.setItem(ANON_UID_KEY, u.id); } catch {}
    return u;
  };

  const resendVerificationEmail = async (email: string) => {
    const res = await fetch("/api/auth/send-verification-email", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        callbackURL: `${window.location.origin}/verify-email`,
      }),
    });
    if (!res.ok) {
      const body = (await res.json()) as { message?: string };
      throw new Error(body.message ?? "Failed to send verification email");
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        isLoading,
        signIn,
        signUp,
        signOut,
        requestPasswordReset,
        resetPassword,
        signInWithSocial,
        resendVerificationEmail,
        signInAnonymous,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
